(function () {
  'use strict';

  const header = document.querySelector('[data-header]');
  if (!header) return;
  // The bar+header wrapper is the sticky unit; `header` stays the ref for menu-open + mega checks.
  const group = document.querySelector('[data-header-group]') || header;

  const sentinel = document.querySelector('[data-header-sentinel]');
  const enableSticky = header.dataset.enableSticky === 'true';

  // =========================================================================
  // 1. Progressive enhancement: upgrade hamburger <a> fallback to <button>
  // =========================================================================
  const fallbackLink = header.querySelector('[data-menu-fallback]');

  if (fallbackLink) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = fallbackLink.className;
    btn.setAttribute('aria-label', 'Open menu');
    btn.setAttribute('aria-expanded', 'false');
    btn.setAttribute('data-menu-toggle', '');
    btn.innerHTML = fallbackLink.innerHTML;
    fallbackLink.replaceWith(btn);
  }

  // =========================================================================
  // 2. Hamburger toggle
  // =========================================================================
  const menuToggle = header.querySelector('[data-menu-toggle]');

  if (menuToggle) {
    menuToggle.addEventListener('click', function () {
      const isOpen = header.dataset.menuOpen === 'true';
      const nextState = !isOpen;

      header.dataset.menuOpen = String(nextState);
      menuToggle.setAttribute('aria-expanded', String(nextState));
      menuToggle.setAttribute('aria-label', nextState ? 'Close menu' : 'Open menu');
    });
  }

  // =========================================================================
  // 3. Sticky header — IntersectionObserver + passive scroll direction tracking
  // =========================================================================
  if (!enableSticky || !sentinel) return;

  // True while the nav menu, mini bag, search, or wishlist drawer is open. Each of
  // these lives partly or wholly in the header (the close control included, on
  // mobile), so the header must never be hidden out from under an open one —
  // whether that hide is scroll-driven or triggered by a resize reflow.
  function anyDrawerOpen() {
    return (
      header.dataset.menuOpen === 'true' ||
      header.dataset.miniBagOpen === 'true' ||
      header.dataset.searchDrawerOpen === 'true' ||
      header.dataset.wishlistDrawerOpen === 'true'
    );
  }

  let lastScrollY = window.scrollY;
  let pastHeader = false;
  // Travel accumulated in the current direction; the header reacts once it passes the threshold.
  let travelled = 0;

  // Hand the header back to normal flow. Called at the top of the document — but a fast scroll-up
  // fling can still be mid-flight through the 300ms reveal transition when this fires (the
  // IntersectionObserver callback lags real scroll position), and releasing also un-hides the
  // announcement bar (shown in static flow, translated off-screen in the sticky "visible" state), so
  // the two states don't actually coincide. Left animating, that mismatch reads as a jump/twitch —
  // so, same as the "entering sticky zone" branch below, the switch is done with transitions
  // suppressed and a forced reflow, making the fixed→relative flip instant either way.
  function releaseSticky() {
    if (!group.hasAttribute('data-header-sticky')) return;
    group.style.transitionDuration = '0s';
    group.removeAttribute('data-header-sticky');
    sentinel.style.height = '';
    travelled = 0;
    lastScrollY = window.scrollY;
    group.offsetHeight; // force reflow before restoring transition
    group.style.transitionDuration = '';
  }

  // Track whether we have scrolled past the sentinel (header left viewport)
  const observer = new IntersectionObserver(
    function (entries) {
      const entry = entries[0];
      pastHeader = !entry.isIntersecting;

      if (!pastHeader) {
        // The sentinel is in view again, but the scroll may still be arriving; wait for the top.
        if (window.scrollY <= 0) releaseSticky();
      } else {
        // Entering sticky zone — set hidden/visible state WITHOUT transition so the
        // position: relative → fixed switch is invisible. This can fire from a resize
        // reflow (not just a scroll) pushing the sentinel out of view — if a drawer is
        // already open at that exact moment, land on visible instead of hidden, or the
        // open drawer's own header-based close control gets hidden out from under it.
        sentinel.style.height = group.offsetHeight + 'px';
        group.style.transitionDuration = '0s';
        group.dataset.headerSticky = anyDrawerOpen() ? 'visible' : 'hidden';
        lastScrollY = window.scrollY; // the run starts here
        travelled = 0;
        group.offsetHeight; // force reflow before restoring transition
        group.style.transitionDuration = '';
      }
    },
    { threshold: 0, rootMargin: '0px' }
  );

  observer.observe(sentinel);

  // Scroll direction listener — only acts once we are past the sentinel. Inertial scroll reverses by
  // a pixel or two as it decelerates, so direction has to be sustained for DIRECTION_THRESHOLD before
  // it counts. Reads are batched into a frame; the attribute is written only when the value changes.
  const DIRECTION_THRESHOLD = 8;
  let ticking = false;

  function applyStickyState() {
    ticking = false;
    if (!pastHeader) {
      if (window.scrollY <= 0) releaseSticky();
      return;
    }
    if (anyDrawerOpen()) return;
    if (header.querySelector('[data-mega-trigger][data-active="true"]')) return;

    const currentScrollY = window.scrollY;
    const delta = currentScrollY - lastScrollY;
    lastScrollY = currentScrollY;
    if (delta === 0) return;

    // A genuine turn restarts the run, so the threshold always measures travel in one direction.
    if (delta > 0 !== travelled > 0) travelled = 0;
    travelled += delta;
    if (Math.abs(travelled) < DIRECTION_THRESHOLD) return;

    const next = travelled > 0 ? 'hidden' : 'visible';
    if (group.dataset.headerSticky !== next) group.dataset.headerSticky = next;
  }

  window.addEventListener(
    'scroll',
    function () {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(applyStickyState);
    },
    { passive: true }
  );

  // =========================================================================
  // 4. Reveal the sticky header on a product Add to Bag (TASK-52346)
  // =========================================================================
  // Ratified 2026-07-14 (Vin Munhoz): adding never opens the mini bag; instead the
  // sticky site nav slides back down so the shopper sees the bag icon's new count.
  // Fire only for adds that originate inside the product block (the main buy-area
  // button or the sticky bar). Recommendation-card and PLP quick-adds broadcast the
  // same event on the document, whose target is not an element within the product,
  // so they are skipped. And only once the header has scrolled past its sentinel —
  // otherwise the header is already on screen and there is nothing to reveal.
  document.addEventListener('cart:added', function (event) {
    if (!pastHeader) return;
    const origin = event.target;
    if (!(origin instanceof Element) || !origin.closest('[data-main-product]')) return;
    group.dataset.headerSticky = 'visible';
  });

  // =========================================================================
  // 5. Reveal the sticky header when a drawer opens
  // =========================================================================
  // The header row is where the mini-bag/search/wishlist close control lives on
  // mobile (this same hamburger button, morphed into an X — see _menu-toggle.css).
  // Without this, opening one of those drawers while the header is scrolled hidden
  // leaves it hidden throughout — no icons, no visible way to close the drawer.
  // Each drawer already stamps its own open state onto [data-header] (the same
  // attributes _menu-toggle.css reads), so reveal on any of them, same as the
  // cart:added case above.
  new MutationObserver(function () {
    if (!pastHeader) return;
    if (anyDrawerOpen()) {
      // Reset the run so a sub-threshold scroll right after the drawer closes can't
      // instantly hide the header again on stale travel accumulated before it opened.
      travelled = 0;
      group.dataset.headerSticky = 'visible';
    }
  }).observe(header, {
    attributes: true,
    attributeFilter: ['data-mini-bag-open', 'data-search-drawer-open', 'data-wishlist-drawer-open'],
  });
})();
